import type { MigrationReplication } from '@schema';
import {
  ReplicationStatus,
  REPLICATION_STATUS_TEXT,
  REPLICATION_STATUS_ICON
} from '@/common/constants/replication.constants';
import i18n from '@/common/i18n';
import { dateFormatter, humanizeMs } from '@/common/utils/formatter.date';
import { bytesToUnit, formatPercent } from '@/common/utils/formatter.number';
import type { MappedProgressUpdate } from '@/common/utils/taskMapper';
import { useMigrationTasksStore } from '@/common/store/pinia/migrationTasks';
import type { MappedMigrationTask } from '@/common/store/pinia/migrationTasks/migrationTasksMapper';

export type { MappedProgressUpdate as ReplicationProgressUpdate };
export type ReplicationTask = MappedMigrationTask;

export interface MappedReplication {
  id: string;
  migrationId: string;
  number: number;
  date: string;
  status: ReplicationStatus;
  replicatedData: number;
  replicaSpeed: number;
  duration: number;
  shutdownInstances?: boolean;
  autoDeploy?: boolean;
  tasks: ReplicationTask[];
  replicationNumber: string;
  /** Max current disk replication progress across tasks (percent), if any > 0. */
  progress: number | null;
  progressFormatted: string;
  dateFormatted: string;
  statusText: string;
  statusIcon: string;
  replicatedDataFormatted: string;
  replicaSpeedFormatted: string;
  durationFormatted: string;
}

export const replicationDiskProgressPercent = (tasks: ReplicationTask[]): number | null => {
  const values = tasks
    .map(t => t.currentProgress)
    .filter((p): p is number => typeof p === 'number' && p > 0);
  if (!values.length) {
    return null;
  }
  return Math.max(...values);
};

const formatSpeed = (bytesPerSecond: number): string => {
  if (!bytesPerSecond || bytesPerSecond === 0) {
    return '0 B/s';
  }
  return `${bytesToUnit(bytesPerSecond, { base: 1000 })}/s`;
};

const mapper = (r: MigrationReplication): MappedReplication => {
  const status = (r.status as ReplicationStatus) ?? ReplicationStatus.UNEXECUTED;
  const missingReplicaMb = r.replica_mb == null;
  const missingReplicaSec = r.replica_sec == null;
  const replicaMb = Number(r.replica_mb) || 0;
  const replicaSec = Number(r.replica_sec) || 0;
  const replicatedData = replicaMb * 1024 * 1024;
  const replicaSpeed = (replicaMb && replicaSec) ? (replicaMb / replicaSec) * 1024 * 1024 : 0;
  const duration = replicaSec * 1000;

  const id = r.id ?? '';
  const date = r.updated_at ?? r.created_at ?? '';
  const tasks = useMigrationTasksStore().getMappedTasksByParent(id);
  const progress = replicationDiskProgressPercent(tasks);

  const dash = i18n.t('figure dash');

  return {
    id,
    migrationId: r.migration_id ?? '',
    number: r.index ?? 0,
    date,
    status,
    replicatedData,
    replicaSpeed,
    duration,
    shutdownInstances: false,
    autoDeploy: false,
    tasks,
    replicationNumber: `#${r.index ?? 0}`,
    progress,
    progressFormatted: progress != null ? formatPercent(progress, 0) : '',
    dateFormatted: dateFormatter(date),
    statusText: REPLICATION_STATUS_TEXT[status],
    statusIcon: REPLICATION_STATUS_ICON[status],
    replicatedDataFormatted: missingReplicaMb ? dash : bytesToUnit(replicatedData),
    replicaSpeedFormatted: (missingReplicaMb || missingReplicaSec) ? dash : formatSpeed(replicaSpeed),
    durationFormatted: missingReplicaSec ? dash : humanizeMs(duration, true)
  };
};

export default mapper;
